import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import type { HelpRequest } from "./useHelpRequests";

export function useAllHelpRequests() {
  const { user } = useAuth();
  const isAdmin = useHasRole("admin");
  const isInstructor = useHasRole("instructor");

  return useQuery({
    queryKey: ["all-help-requests", user?.id, isAdmin, isInstructor],
    queryFn: async () => {
      if (!user?.id) return [];

      // For mentors: filter by their selected topics
      // Admins and instructors see all requests
      let mentorTopicIds: string[] = [];
      
      if (!isAdmin && !isInstructor) {
        // Get mentor's selected topic IDs
        const { data: topicSelections } = await supabase
          .from("mentor_topic_selections")
          .select("topic_id")
          .eq("mentor_id", user.id);
        
        mentorTopicIds = topicSelections?.map((t) => t.topic_id) || [];
        
        // If mentor has no topics selected, they see no requests
        if (mentorTopicIds.length === 0) {
          return [];
        }
      }

      let query = supabase
        .from("help_requests")
        .select("*")
        .order("created_at", { ascending: false });

      // Mentors only see requests matching their selected topics
      if (!isAdmin && !isInstructor) {
        query = query.in("topic_id", mentorTopicIds);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch author profiles, course info, and topic info
      const userIds = [...new Set(data.map((r) => r.user_id))];
      const resolverIds = [...new Set(data.filter((r) => r.resolved_by).map((r) => r.resolved_by!))];
      const allUserIds = [...new Set([...userIds, ...resolverIds])];
      const courseIds = [...new Set(data.map((r) => r.course_id))];
      const topicIds = [...new Set(data.filter((r) => r.topic_id).map((r) => r.topic_id!))];

      const [profilesResult, coursesResult, topicsResult] = await Promise.all([
        allUserIds.length > 0 
          ? supabase.from("profiles").select("id, full_name, avatar_url").in("id", allUserIds)
          : Promise.resolve({ data: [] as { id: string; full_name: string; avatar_url: string | null }[] }),
        courseIds.length > 0
          ? supabase.from("courses").select("id, title").in("id", courseIds)
          : Promise.resolve({ data: [] as { id: string; title: string }[] }),
        topicIds.length > 0
          ? supabase.from("mentor_topics").select("id, name").in("id", topicIds)
          : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      ]);

      const profileMap = new Map((profilesResult.data || []).map((p) => [p.id, p] as const));
      const courseMap = new Map((coursesResult.data || []).map((c) => [c.id, c] as const));
      const topicMap = new Map((topicsResult.data || []).map((t) => [t.id, t] as const));

      return data.map((request) => ({
        ...request,
        status: request.status as HelpRequest["status"],
        author: profileMap.get(request.user_id),
        resolver: request.resolved_by ? profileMap.get(request.resolved_by) : null,
        course: courseMap.get(request.course_id),
        topic: request.topic_id ? topicMap.get(request.topic_id) : null,
      })) as (HelpRequest & { course?: { id: string; title: string }; topic?: { id: string; name: string } })[];
    },
    enabled: !!user?.id,
  });
}

export function usePendingHelpRequestCount() {
  const { user } = useAuth();
  const isAdmin = useHasRole("admin");
  const isInstructor = useHasRole("instructor");

  return useQuery({
    queryKey: ["pending-help-request-count", user?.id, isAdmin, isInstructor],
    queryFn: async () => {
      if (!user?.id || (!isAdmin && !isInstructor)) return 0;

      const { count, error } = await supabase
        .from("help_requests")
        .select("*", { count: "exact", head: true })
        .in("status", ["pending", "in_progress"]);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id && (isAdmin || isInstructor),
  });
}
